import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import CustomTableWithPagination from "../../components/customTable/CustomTable";
import CustomButton from "../../components/CommonButton/CommonButton";
import { getFormattedDate } from "../../utils/commonFunctions";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../components/loader/Loader";

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

interface LoyaltyProgram {
  Id: number;
  PointActionId: number;
  Points: number;
  Created: string;
  Updated: string;
  MerchantId: number;
  Item: string;
  IsActive: number;
}

interface LoyaltyProgramForm {
  Id?: number;
  BusinessId: string;
  PointActionId: number;
  Points: number | string; // Allow string for easier input handling
  Item: string;
  Item2?: string; // For Multi Count program
  Item3?: string; // For Multi Count program (optional)
  IsActive: number;
}

const BusinessProgramPage: React.FC = () => {
  const { businessId } = useParams<any>();
  const [programs, setPrograms] = useState<LoyaltyProgram[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState<LoyaltyProgram | null>(
    null
  );
  const [formData, setFormData] = useState<LoyaltyProgramForm>({
    BusinessId: businessId || "",
    PointActionId: 1,
    Points: 0,
    Item: "Visit",
    Item2: "",
    Item3: "",
    IsActive: 1
  });
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [createAndUpdateLoading, setCreateAndUpdateLoading] = useState(false);
  // Track field interactions to show validation errors only after user interaction
  const [touchedFields, setTouchedFields] = useState<{
    Points: boolean;
    Item: boolean;
    Item2: boolean;
    Item3: boolean;
  }>({
    Points: false,
    Item: false,
    Item2: false,
    Item3: false
  });

  // Track if third item is shown for Multi Count
  const [showThirdItem, setShowThirdItem] = useState<boolean>(false);

  // Track if form has been submitted to show all validation errors
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Modal-specific error state
  const [modalError, setModalError] = useState<string | null>(null);

  // Track if modal is in error-only mode (no form, only error message)
  const [isErrorOnlyMode, setIsErrorOnlyMode] = useState<boolean>(false);

  // Confirmation modal states for multi-count activation/deactivation
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    program: LoyaltyProgram | null;
    relatedPrograms: LoyaltyProgram[];
    availablePrograms?: LoyaltyProgram[]; // For multi-select activation
    message: string;
    action?: "activate" | "deactivate";
  }>({
    open: false,
    program: null,
    relatedPrograms: [],
    availablePrograms: [],
    message: "",
    action: "deactivate"
  });
  const [isConfirmModalLoading, setIsConfirmModalLoading] =
    useState<boolean>(false);
  const [selectedProgramsToActivate, setSelectedProgramsToActivate] = useState<
    number[]
  >([]);
  const [confirmModalError, setConfirmModalError] = useState<string | null>(
    null
  );

  const activeProgramsCount = useMemo(
    () => programs.filter((p) => p.IsActive === 1).length,
    [programs]
  );
  const availableActiveSlots = Math.max(0, 3 - activeProgramsCount);

  // Form validation function
  const isFormValid = () => {
    // Check if required fields are filled
    if (
      !formData.PointActionId ||
      !formData.Points ||
      Number(formData.Points) <= 0
    ) {
      return false;
    }

    // Check if Item is filled based on action type
    const selectedAction = pointActionOptions.find(
      (opt) => opt.value === formData.PointActionId
    );

    const isMultiCount = formData.PointActionId === 5;

    if (selectedAction?.itemType === "fixed") {
      // For fixed actions, ensure Item has a value (API sets it server-side)
      return !!(formData.Item && formData.Item.trim());
    } else if (selectedAction?.itemType === "textbox") {
      // For Multi Count, validation depends on create vs edit
      if (isMultiCount) {
        // When editing, only Item is required (editing one item at a time)
        if (editingProgram) {
          if (!formData.Item || formData.Item.trim() === "") {
            return false;
          }
          return true;
        }
        // When creating, both Item and Item2 are required
        if (!formData.Item || formData.Item.trim() === "") {
          return false;
        }
        if (!formData.Item2 || formData.Item2.trim() === "") {
          return false;
        }
        return true;
      }

      // For other textbox actions, Item should not be empty
      if (!formData.Item || formData.Item.trim() === "") {
        return false;
      }

      // Additional validation for Dollars
      if (selectedAction?.label === "Dollars") {
        const dollarAmount = parseFloat(formData.Item);
        return (
          !isNaN(dollarAmount) &&
          dollarAmount > 0 &&
          Number.isInteger(dollarAmount)
        );
      }
    }

    return true;
  };

  // Helper function to check if field should show error
  const shouldShowError = (fieldName: keyof typeof touchedFields) => {
    return touchedFields[fieldName] || hasSubmitted;
  };

  const pointActionOptions = [
    { value: 2, label: "Count", itemType: "textbox" },
    { value: 3, label: "Visit", itemType: "fixed" },
    { value: 4, label: "Dollars", itemType: "textbox" },
    { value: 5, label: "Multi Count", itemType: "textbox" }
  ];

  // Find related multi-count programs (same PointActionId = 5 and same Points)
  // Related programs are those created together as part of the same Multi Count group
  const findRelatedMultiCountPrograms = useCallback(
    (program: LoyaltyProgram): LoyaltyProgram[] => {
      if (program.PointActionId !== 5) return [];

      // Convert Points to numbers for comparison to handle type inconsistencies
      const programPoints = Number(program.Points);

      // First, try to find programs with same Points value (primary grouping method)
      let related = programs.filter(
        (p) =>
          p.Id !== program.Id &&
          p.PointActionId === 5 &&
          Number(p.Points) === programPoints
      );

      // If no programs found with same Points, try grouping by creation time
      // (Multi Count programs created together should have similar timestamps)
      if (related.length === 0 && program.Created) {
        const programCreatedTime = new Date(program.Created).getTime();
        const timeWindow = 5 * 60 * 1000; // 5 minutes window

        related = programs.filter(
          (p) =>
            p.Id !== program.Id &&
            p.PointActionId === 5 &&
            p.Created &&
            Math.abs(new Date(p.Created).getTime() - programCreatedTime) <=
              timeWindow
        );
      }

      return related;
    },
    [programs]
  );

  const fetchPrograms = useCallback(async () => {
    if (!businessId) return;

    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${apiEndpoint}/loyaltyprogram/support/business/loyaltyprograms/${businessId}`,
        {
          headers: {
            Authorization: `bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        }
      );
      setPrograms(response.data || []);
    } catch (error) {
      console.error("Failed to fetch programs:", error);
      setNotification({
        type: "error",
        message: "Failed to fetch loyalty programs"
      });
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const handleOpenModal = (program?: LoyaltyProgram) => {
    if (program) {
      setEditingProgram(program);
      setFormData({
        Id: program.Id,
        BusinessId: businessId || "",
        PointActionId: program.PointActionId,
        Points: program.Points,
        Item: program.Item,
        Item2: "",
        Item3: "",
        IsActive: program.IsActive
      });
      setShowThirdItem(false);
      setIsErrorOnlyMode(false);
    } else {
      // Check if max 3 active programs already exist
      if (activeProgramsCount >= 3) {
        setModalError(
          "Max, three programs can be active at a time. Please de-activate another program to create new one"
        );
        setIsErrorOnlyMode(true);
        setEditingProgram(null);
        setFormData({
          BusinessId: businessId || "",
          PointActionId: 2,
          Points: 0,
          Item: "",
          Item2: "",
          Item3: "",
          IsActive: 1
        });
      } else {
        setIsErrorOnlyMode(false);
        setModalError(null); // Clear any previous modal errors
        setEditingProgram(null);
        setFormData({
          BusinessId: businessId || "",
          PointActionId: 2, // Default to Count
          Points: 0,
          Item: "",
          Item2: "",
          Item3: "",
          IsActive: 1
        });
      }
    }
    // Reset validation state when opening modal
    setTouchedFields({
      Points: false,
      Item: false,
      Item2: false,
      Item3: false
    });
    setShowThirdItem(false);
    setHasSubmitted(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingProgram(null);
    setFormData({
      BusinessId: businessId || "",
      PointActionId: 2, // Default to Count
      Points: 0,
      Item: "",
      Item2: "",
      Item3: "",
      IsActive: 1
    });
    // Reset validation state when closing modal
    setTouchedFields({
      Points: false,
      Item: false,
      Item2: false,
      Item3: false
    });
    setHasSubmitted(false);
    setShowThirdItem(false);
    setModalError(null); // Clear modal errors
    setIsErrorOnlyMode(false);
    setCreateAndUpdateLoading(false); // Reset loading state
    // Reset confirmation modal state
    setSelectedProgramsToActivate([]);
    setConfirmModalError(null);
    setConfirmModal({
      open: false,
      program: null,
      relatedPrograms: [],
      availablePrograms: [],
      message: "",
      action: "deactivate"
    });
  };

  const handleSubmit = async () => {
    // Mark as submitted to show all validation errors
    setHasSubmitted(true);
    setCreateAndUpdateLoading(true);

    // Check if form is valid before proceeding
    if (!isFormValid()) {
      setCreateAndUpdateLoading(false);
      return;
    }

    try {
      const accessToken = localStorage.getItem("accessToken");
      const isMultiCount = formData.PointActionId === 5;

      // Check if Multi Count creation is allowed
      if (isMultiCount && !editingProgram) {
        const optionalThirdFilled =
          formData.Item3 && formData.Item3.trim() !== "";
        const itemsToCreate = 2 + (optionalThirdFilled ? 1 : 0);
        if (activeProgramsCount + itemsToCreate > 3) {
          setModalError(
            "Cannot create Multi Count program. Maximum 3 active programs allowed. Reduce the number of items or deactivate another program."
          );
          setCreateAndUpdateLoading(false);
          return;
        }
      }

      // Validate Dollars input
      if (formData.PointActionId === 4 && formData.Item) {
        const dollarAmount = parseFloat(formData.Item);
        if (
          isNaN(dollarAmount) ||
          dollarAmount <= 0 ||
          !Number.isInteger(dollarAmount)
        ) {
          setModalError("Please enter a valid positive integer dollar amount");
          setCreateAndUpdateLoading(false);
          return;
        }
      }

      if (editingProgram) {
        // Update existing program - include IsActive in payload
        const dataToSend = {
          ...formData,
          BusinessId: businessId
        };
        // Remove Item2 and Item3 for edit mode (Multi Count programs are edited individually)
        const { Item2, Item3, ...updateData } = dataToSend;

        await axios.put(
          `${apiEndpoint}/loyaltyprogram/support/business/loyaltyprogram`,
          updateData,
          {
            headers: {
              Authorization: `bearer ${accessToken}`,
              "Content-Type": "application/json"
            }
          }
        );
        setCreateAndUpdateLoading(false); // Reset loading on success
        setNotification({
          type: "success",
          message: "Loyalty program updated successfully"
        });
        handleCloseModal(); // Only close on success
        fetchPrograms();
      } else {
        if (isMultiCount) {
          // Multi Count: Call API for each item (2 required + 1 optional if filled)
          const payloads = [
            {
              BusinessId: businessId,
              PointActionId: formData.PointActionId,
              Points: Number(formData.Points),
              Item: formData.Item
            },
            {
              BusinessId: businessId,
              PointActionId: formData.PointActionId,
              Points: Number(formData.Points),
              Item: formData.Item2
            }
          ];

          // Add third item if it's filled
          if (formData.Item3 && formData.Item3.trim() !== "") {
            payloads.push({
              BusinessId: businessId,
              PointActionId: formData.PointActionId,
              Points: Number(formData.Points),
              Item: formData.Item3
            });
          }

          await Promise.all(
            payloads.map((payload) =>
              axios.post(
                `${apiEndpoint}/loyaltyprogram/support/business/loyaltyprogram`,
                payload,
                {
                  headers: {
                    Authorization: `bearer ${accessToken}`,
                    "Content-Type": "application/json"
                  }
                }
              )
            )
          );

          setCreateAndUpdateLoading(false); // Reset loading on success
          const itemCount = payloads.length;
          setNotification({
            type: "success",
            message: `Multi Count program with ${itemCount} item(s) created successfully`
          });
          handleCloseModal(); // Only close on success
          fetchPrograms();
        } else {
          // Create new program - exclude IsActive from payload
          const { IsActive, Item2, Item3, ...dataToSend } = formData;
          const payload = {
            ...dataToSend,
            BusinessId: businessId
          };

          await axios.post(
            `${apiEndpoint}/loyaltyprogram/support/business/loyaltyprogram`,
            payload,
            {
              headers: {
                Authorization: `bearer ${accessToken}`,
                "Content-Type": "application/json"
              }
            }
          );
          setCreateAndUpdateLoading(false); // Reset loading on success
          setNotification({
            type: "success",
            message: "Loyalty program created successfully"
          });
          handleCloseModal(); // Only close on success
          fetchPrograms();
        }
      }
    } catch (error: any) {
      // Show error in modal instead of closing it
      setModalError(
        error.response?.data?.message || "Operation failed. Please try again."
      );
      setCreateAndUpdateLoading(false);
    }
  };

  const columns: any[] = [
    {
      id: "Id",
      label: "ID",
      format: (value: any) => (value ? value : "-")
    },
    {
      id: "PointActionId",
      label: "Action",
      format: (value: number) => {
        const action = pointActionOptions.find((opt) => opt.value === value);
        return action?.label || value;
      }
    },
    {
      id: "Created",
      label: "Created Date",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "MerchantId",
      label: "Merchant ID",
      format: (value: any) => (value ? value : "-")
    },
    {
      id: "Points",
      label: "Points",
      format: (value: any) => (value ? value : "-")
    },
    {
      id: "Item",
      label: "Item",
      format: (value: any) => (value ? value : "-")
    },
    {
      id: "IsActive",
      label: "Status",
      format: (value: number) => {
        const isActive = value === 1;
        return (
          <Chip
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "success" : "default"}
            size="small"
            sx={{
              backgroundColor: isActive ? "#4caf50" : "#9e9e9e",
              color: "white",
              fontWeight: 500
            }}
          />
        );
      }
    },
    {
      id: "actions",
      label: "Actions",
      format: (value: any, row: LoyaltyProgram) => (
        <IconButton
          onClick={() => handleOpenModal(row)}
          size="small"
          color="primary"
        >
          <EditIcon
            sx={{
              color: "#192F67"
            }}
          />
        </IconButton>
      )
    }
  ];
  return (
    <div>
      {notification && (
        <Alert
          severity={notification.type}
          sx={{ mb: 2 }}
          onClose={() => setNotification(null)}
        >
          {notification.message}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mb={2}
        gap={"10px"}
      >
        <CustomButton onClick={() => handleOpenModal()}>
          Add Program
        </CustomButton>
        <RefreshIcon
          onClick={() => {
            setNotification(null);
            fetchPrograms();
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Loader />
        </Box>
      ) : (
        <CustomTableWithPagination
          columns={columns}
          data={programs}
          apiStatus="idle"
          isShowPagination={false}
        />
      )}

      {/* Add/Edit Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {editingProgram ? "Edit Program" : "Add Program"}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* Show modal error alert */}
            {modalError && (
              <Alert
                severity="error"
                onClose={() => {
                  setModalError(null);
                  setIsErrorOnlyMode(false);
                }}
                sx={{ mb: 1 }}
              >
                {modalError}
              </Alert>
            )}

            {/* Only show form content if not in error-only mode */}
            {!isErrorOnlyMode && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Point Action</InputLabel>
                  <Select
                    value={formData.PointActionId}
                    onChange={(e) => {
                      const selectedAction = e.target.value as number;
                      const action = pointActionOptions.find(
                        (opt) => opt.value === selectedAction
                      );
                      const isMultiCount = selectedAction === 5;
                      const allowThirdMultiCountItem =
                        isMultiCount &&
                        !editingProgram &&
                        availableActiveSlots >= 3;

                      // Check if Multi Count is being selected and if it's allowed
                      if (isMultiCount && !editingProgram) {
                        if (availableActiveSlots < 2) {
                          setModalError(
                            "Cannot create Multi Count program. Maximum 3 active programs allowed. Multi Count creates 2-3 programs, and you already have 2 active programs."
                          );
                          return; // Don't change the selection
                        }
                      }

                      // Clear any previous errors
                      setModalError(null);

                      setFormData({
                        ...formData,
                        PointActionId: selectedAction,
                        Item: action?.itemType === "fixed" ? "Visit" : "",
                        Item2: isMultiCount ? formData.Item2 || "" : "",
                        Item3:
                          isMultiCount && allowThirdMultiCountItem
                            ? formData.Item3 || ""
                            : ""
                      });
                      if (!isMultiCount || !allowThirdMultiCountItem) {
                        setShowThirdItem(false);
                      }
                    }}
                    label="Point Action"
                  >
                    {pointActionOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Points"
                  type="number"
                  value={formData.Points}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      Points: parseInt(e.target.value)
                    })
                  }
                  onBlur={() =>
                    setTouchedFields((prev) => ({ ...prev, Points: true }))
                  }
                  helperText="Points must be greater than 0"
                  error={
                    shouldShowError("Points") && Number(formData.Points) <= 0
                  }
                />

                {(() => {
                  const selectedAction = pointActionOptions.find(
                    (opt) => opt.value === formData.PointActionId
                  );
                  const isMultiCount = formData.PointActionId === 5;
                  const isDollars = formData.PointActionId === 4;

                  if (selectedAction?.itemType === "fixed") {
                    return (
                      <TextField
                        fullWidth
                        label="Item"
                        value={formData.Item}
                        disabled
                        helperText="Fixed value for Visit action"
                      />
                    );
                  } else if (isMultiCount) {
                    // When editing, only show single Item field (one item at a time)
                    if (editingProgram) {
                      return (
                        <TextField
                          fullWidth
                          label="Item"
                          value={formData.Item}
                          onChange={(e) =>
                            setFormData({ ...formData, Item: e.target.value })
                          }
                          onBlur={() =>
                            setTouchedFields((prev) => ({
                              ...prev,
                              Item: true
                            }))
                          }
                          error={
                            shouldShowError("Item") &&
                            (!formData.Item || formData.Item.trim() === "")
                          }
                          helperText={
                            shouldShowError("Item") &&
                            (!formData.Item || formData.Item.trim() === "")
                              ? "Required"
                              : ""
                          }
                          required
                        />
                      );
                    }
                    // When creating, show multiple items (Item1, Item2, Item3)
                    const canAddThirdMultiCountItem = availableActiveSlots >= 3;
                    return (
                      <>
                        <TextField
                          fullWidth
                          label="Item 1"
                          value={formData.Item}
                          onChange={(e) =>
                            setFormData({ ...formData, Item: e.target.value })
                          }
                          onBlur={() =>
                            setTouchedFields((prev) => ({
                              ...prev,
                              Item: true
                            }))
                          }
                          error={
                            shouldShowError("Item") &&
                            (!formData.Item || formData.Item.trim() === "")
                          }
                          helperText={
                            shouldShowError("Item") &&
                            (!formData.Item || formData.Item.trim() === "")
                              ? "Required"
                              : ""
                          }
                          required
                        />
                        <TextField
                          fullWidth
                          label="Item 2"
                          value={formData.Item2 || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, Item2: e.target.value })
                          }
                          onBlur={() =>
                            setTouchedFields((prev) => ({
                              ...prev,
                              Item2: true
                            }))
                          }
                          error={
                            shouldShowError("Item2") &&
                            (!formData.Item2 || formData.Item2.trim() === "")
                          }
                          helperText={
                            shouldShowError("Item2") &&
                            (!formData.Item2 || formData.Item2.trim() === "")
                              ? "Required"
                              : ""
                          }
                          required
                        />
                        {canAddThirdMultiCountItem && showThirdItem && (
                          <TextField
                            fullWidth
                            label="Item 3 (Optional)"
                            value={formData.Item3 || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                Item3: e.target.value
                              })
                            }
                            onBlur={() =>
                              setTouchedFields((prev) => ({
                                ...prev,
                                Item3: true
                              }))
                            }
                            helperText="Optional third item"
                          />
                        )}
                        {canAddThirdMultiCountItem && !showThirdItem && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1
                            }}
                          >
                            <IconButton
                              onClick={() => setShowThirdItem(true)}
                              disabled={createAndUpdateLoading}
                              sx={{
                                color: "#192F67",
                                border: "1px solid #192F67",
                                "&:hover": {
                                  backgroundColor: "#192F67",
                                  color: "white"
                                }
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                            <Box
                              component="span"
                              sx={{ fontSize: "14px", color: "#666" }}
                            >
                              Add optional third item
                            </Box>
                          </Box>
                        )}
                      </>
                    );
                  } else if (selectedAction?.itemType === "textbox") {
                    return (
                      <TextField
                        fullWidth
                        label="Item"
                        value={formData.Item}
                        onChange={(e) => {
                          let value = e.target.value;

                          // For Dollars, only allow positive integers
                          if (isDollars) {
                            // Remove any non-digit characters
                            value = value.replace(/\D/g, "");
                            // Ensure it's not empty
                            if (value === "") {
                              value = "";
                            }
                          }

                          setFormData({ ...formData, Item: value });
                        }}
                        onBlur={() =>
                          setTouchedFields((prev) => ({ ...prev, Item: true }))
                        }
                        placeholder={
                          isDollars ? "Enter $ amount" : "Enter text"
                        }
                        helperText={
                          isDollars
                            ? "Enter dollar amount (e.g., 10)"
                            : "Enter text value"
                        }
                        error={
                          shouldShowError("Item") &&
                          (!formData.Item || formData.Item.trim() === "")
                        }
                        InputProps={{
                          startAdornment: isDollars ? (
                            <InputAdornment position="start">
                              <AttachMoneyIcon />
                            </InputAdornment>
                          ) : undefined
                        }}
                      />
                    );
                  }
                  return null;
                })()}

                {/* Show IsActive checkbox only when editing */}
                {editingProgram && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.IsActive === 1}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // User is trying to activate
                            const currentProgram = programs.find(
                              (p) => p.Id === editingProgram.Id
                            );

                            if (!currentProgram) {
                              return;
                            }

                            const otherActiveCount = programs.filter(
                              (p) =>
                                p.IsActive === 1 && p.Id !== editingProgram.Id
                            ).length;

                            if (otherActiveCount >= 3) {
                              setModalError(
                                "Maximum 3 active programs allowed. Please deactivate another program first."
                              );
                              return;
                            }

                            // Check if activating a multi-count program
                            if (currentProgram.PointActionId === 5) {
                              // If no other active programs exist, we need to activate at least one related multi-count
                              if (otherActiveCount === 0) {
                                // Get all inactive programs (all types, not just multi-count)
                                const allInactivePrograms = programs.filter(
                                  (p) =>
                                    p.IsActive !== 1 &&
                                    p.Id !== currentProgram.Id
                                );

                                if (allInactivePrograms.length > 0) {
                                  // Show multi-select dialog with all available programs
                                  setSelectedProgramsToActivate([]); // Reset selection
                                  setConfirmModalError(null); // Reset error
                                  setConfirmModal({
                                    open: true,
                                    program: currentProgram,
                                    relatedPrograms: [],
                                    availablePrograms: allInactivePrograms,
                                    message: `You are activating a Multi Count program, but there are no other active programs. Multi Count programs must be active in groups of 2-3. Please select at least one additional program to activate:`,
                                    action: "activate"
                                  });
                                  return;
                                } else {
                                  // No inactive programs found - show error
                                  setModalError(
                                    "Cannot activate a single Multi Count program. Multi Count programs must be active in groups of 2-3. Please create another program first."
                                  );
                                  return;
                                }
                              }
                            }

                            // Allow activation for non-multi-count or if other programs are already active
                            setModalError(null);
                            setFormData({
                              ...formData,
                              IsActive: 1
                            });
                          } else {
                            // User is trying to deactivate
                            const currentProgram = programs.find(
                              (p) => p.Id === editingProgram.Id
                            );

                            if (!currentProgram) {
                              return;
                            }

                            // Count all active programs
                            const allActiveMultiCountPrograms = programs.filter(
                              (p) => p.PointActionId === 5 && p.IsActive === 1
                            );
                            const activeNonMultiCountPrograms = programs.filter(
                              (p) => p.PointActionId !== 5 && p.IsActive === 1
                            );
                            const totalActivePrograms =
                              allActiveMultiCountPrograms.length +
                              activeNonMultiCountPrograms.length;

                            // Check if it's a multi-count program
                            if (currentProgram.PointActionId === 5) {
                              // Test case 1: Exactly 2 active multi-count programs total AND no other active programs
                              // In this case, we need to deactivate both to prevent crash
                              if (
                                allActiveMultiCountPrograms.length === 2 &&
                                activeNonMultiCountPrograms.length === 0
                              ) {
                                // Find the other active multi-count program
                                const otherActiveMultiCount =
                                  allActiveMultiCountPrograms.find(
                                    (p) => p.Id !== currentProgram.Id
                                  );

                                if (otherActiveMultiCount) {
                                  const relatedPrograms =
                                    findRelatedMultiCountPrograms(
                                      currentProgram
                                    );

                                  // Check if the other active multi-count is related (same Points)
                                  const isOtherRelated = relatedPrograms.some(
                                    (p) => p.Id === otherActiveMultiCount.Id
                                  );

                                  // If they're related or if there are exactly 2 active multi-count total
                                  // Show confirmation to deactivate both
                                  setConfirmModal({
                                    open: true,
                                    program: currentProgram,
                                    relatedPrograms: isOtherRelated
                                      ? [otherActiveMultiCount]
                                      : [],
                                    message: `You have exactly 2 active Multi Count programs and no other active programs. Deactivating one will leave only one active Multi Count program, which may cause issues. Do you want to deactivate both Multi Count programs?`,
                                    action: "deactivate"
                                  });
                                  return;
                                }
                              }

                              // Test case 2: More than 2 active multi-count programs
                              // Just deactivate the one being edited, no confirmation needed
                              if (allActiveMultiCountPrograms.length > 2) {
                                // Allow direct deactivation
                                setModalError(null);
                                setFormData({
                                  ...formData,
                                  IsActive: 0
                                });
                                return;
                              }

                              // Test case 3: 1 active multi-count and other type programs are active
                              // Just deactivate without asking
                              if (
                                allActiveMultiCountPrograms.length === 1 &&
                                activeNonMultiCountPrograms.length > 0
                              ) {
                                // Allow direct deactivation
                                setModalError(null);
                                setFormData({
                                  ...formData,
                                  IsActive: 0
                                });
                                return;
                              }

                              // Test case 4: 1 active multi-count and no other active programs
                              // Allow deactivation (user can deactivate all programs)
                              if (
                                allActiveMultiCountPrograms.length === 1 &&
                                activeNonMultiCountPrograms.length === 0
                              ) {
                                // Allow direct deactivation
                                setModalError(null);
                                setFormData({
                                  ...formData,
                                  IsActive: 0
                                });
                                return;
                              }
                            } else {
                              // For non-multi-count programs: Check if deactivating would leave a single multi-count program
                              // If there are exactly 2 active programs total (1 multi-count + 1 non-multi-count)
                              // and we're deactivating the non-multi-count one, we should also deactivate the multi-count
                              if (
                                totalActivePrograms === 2 &&
                                allActiveMultiCountPrograms.length === 1 &&
                                activeNonMultiCountPrograms.length === 1
                              ) {
                                // Find the active multi-count program
                                const activeMultiCountProgram =
                                  allActiveMultiCountPrograms[0];

                                if (activeMultiCountProgram) {
                                  // Find related multi-count programs (in case there are multiple in the group)
                                  const relatedPrograms =
                                    findRelatedMultiCountPrograms(
                                      activeMultiCountProgram
                                    );

                                  // Get all active related multi-count programs
                                  const activeRelatedPrograms = [
                                    activeMultiCountProgram,
                                    ...relatedPrograms.filter(
                                      (p) => p.IsActive === 1
                                    )
                                  ];

                                  setConfirmModal({
                                    open: true,
                                    program: currentProgram,
                                    relatedPrograms: activeRelatedPrograms,
                                    message: `You have exactly 2 active programs (1 Multi Count and 1 other). Deactivating this program would leave only one active Multi Count program, which is not allowed. Do you want to deactivate this program and all related Multi Count programs?`,
                                    action: "deactivate"
                                  });
                                  return;
                                }
                              }
                            }

                            // For non-multi-count programs or if no special case matched, allow deactivation
                            setModalError(null);
                            setFormData({
                              ...formData,
                              IsActive: 0
                            });
                          }
                        }}
                        disabled={createAndUpdateLoading}
                        sx={{
                          "&.Mui-checked": {
                            color: "#192F67"
                          }
                        }}
                      />
                    }
                    label="Active"
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={handleCloseModal}>Cancel</CustomButton>
          {!isErrorOnlyMode && (
            <CustomButton
              onClick={handleSubmit}
              disabled={createAndUpdateLoading || !isFormValid()}
              isLoading={createAndUpdateLoading}
            >
              {createAndUpdateLoading ? (
                <CircularProgress size={20} />
              ) : editingProgram ? (
                "Update"
              ) : (
                "Create"
              )}
            </CustomButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Multi-Count Activation/Deactivation */}
      <Dialog
        open={confirmModal.open}
        onClose={() => {
          setSelectedProgramsToActivate([]);
          setConfirmModalError(null);
          setConfirmModal({
            open: false,
            program: null,
            relatedPrograms: [],
            availablePrograms: [],
            message: "",
            action: "deactivate"
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              component="span"
              sx={{ fontWeight: 600, fontSize: "20px", color: "#192F67" }}
            >
              {confirmModal.action === "activate"
                ? "Activate Multi Count Program"
                : "Deactivate Multi Count Program"}
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => {
                setSelectedProgramsToActivate([]);
                setConfirmModalError(null);
                setConfirmModal({
                  open: false,
                  program: null,
                  relatedPrograms: [],
                  availablePrograms: [],
                  message: "",
                  action: "deactivate"
                });
              }}
              disabled={isConfirmModalLoading}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>{confirmModal.message}</Typography>
          {confirmModalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {confirmModalError}
            </Alert>
          )}
          {confirmModal.action === "activate" &&
            confirmModal.availablePrograms &&
            confirmModal.availablePrograms.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Select programs to activate (maximum 2):
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 2
                  }}
                >
                  {confirmModal.availablePrograms.map((program) => {
                    // Check if it's a related multi-count program (same Points value and PointActionId = 5)
                    const isRelated =
                      confirmModal.program &&
                      program.PointActionId === 5 &&
                      confirmModal.program.PointActionId === 5 &&
                      Number(program.Points) ===
                        Number(confirmModal.program.Points);
                    const actionLabel =
                      pointActionOptions.find(
                        (opt) => opt.value === program.PointActionId
                      )?.label || "Unknown";

                    return (
                      <Box
                        key={program.Id}
                        sx={{
                          mb: 2,
                          "&:last-child": {
                            mb: 0
                          }
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedProgramsToActivate.includes(
                                program.Id
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Maximum 2 programs can be selected (current program + 2 others = 3 total max)
                                  if (selectedProgramsToActivate.length >= 2) {
                                    setConfirmModalError(
                                      "Maximum 3 active programs allowed. You can only select 2 additional programs."
                                    );
                                    return;
                                  }
                                  setConfirmModalError(null); // Clear any previous errors
                                  setSelectedProgramsToActivate([
                                    ...selectedProgramsToActivate,
                                    program.Id
                                  ]);
                                } else {
                                  setConfirmModalError(null); // Clear any previous errors
                                  setSelectedProgramsToActivate(
                                    selectedProgramsToActivate.filter(
                                      (id) => id !== program.Id
                                    )
                                  );
                                }
                              }}
                              disabled={
                                !selectedProgramsToActivate.includes(
                                  program.Id
                                ) && selectedProgramsToActivate.length >= 2
                              }
                              sx={{
                                "&.Mui-checked": {
                                  color: "#192F67"
                                },
                                mr: 2
                              }}
                            />
                          }
                          label={
                            <Box sx={{ flex: 1, pt: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  mb: isRelated ? 0.5 : 0,
                                  lineHeight: 1.5
                                }}
                              >
                                <strong>{program.Item}</strong> - {actionLabel}{" "}
                                ({program.Points} points)
                              </Typography>
                              {isRelated && (
                                <Chip
                                  label="Related Multi Count"
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: "0.7rem",
                                    backgroundColor: "#e3f2fd",
                                    color: "#1976d2",
                                    fontWeight: 500
                                  }}
                                />
                              )}
                            </Box>
                          }
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            m: 0,
                            p: 1.5,
                            borderRadius: 1,
                            border: "1px solid #e0e0e0",
                            backgroundColor:
                              selectedProgramsToActivate.includes(program.Id)
                                ? "#f0f7ff"
                                : "transparent",
                            "&:hover": {
                              backgroundColor:
                                selectedProgramsToActivate.includes(program.Id)
                                  ? "#e3f2fd"
                                  : "#f9f9f9",
                              borderColor: "#1976d2"
                            },
                            transition: "all 0.2s ease-in-out"
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
                {selectedProgramsToActivate.length === 0 && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Please select at least one program to activate (maximum 2)
                  </Typography>
                )}
                {selectedProgramsToActivate.length === 2 && (
                  <Typography
                    variant="caption"
                    color="info"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Maximum 2 programs selected (3 total active programs limit)
                  </Typography>
                )}
              </Box>
            )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => {
              setSelectedProgramsToActivate([]);
              setConfirmModalError(null);
              setConfirmModal({
                open: false,
                program: null,
                relatedPrograms: [],
                availablePrograms: [],
                message: "",
                action: "deactivate"
              });
            }}
            disabled={isConfirmModalLoading}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={async () => {
              if (!confirmModal.program || !businessId) return;

              setIsConfirmModalLoading(true);
              try {
                const accessToken = localStorage.getItem("accessToken");
                const isActivating = confirmModal.action === "activate";

                if (isActivating) {
                  // Validate that at least one program is selected and max 2
                  if (selectedProgramsToActivate.length === 0) {
                    setConfirmModalError(
                      "Please select at least one program to activate"
                    );
                    setIsConfirmModalLoading(false);
                    return;
                  }
                  if (selectedProgramsToActivate.length > 2) {
                    setConfirmModalError(
                      "Maximum 2 programs can be selected (3 total active programs limit)"
                    );
                    setIsConfirmModalLoading(false);
                    return;
                  }

                  // Get selected programs from availablePrograms
                  const selectedPrograms =
                    confirmModal.availablePrograms?.filter((p) =>
                      selectedProgramsToActivate.includes(p.Id)
                    ) || [];

                  // Include the current program being activated
                  const allProgramsToActivate = [
                    confirmModal.program,
                    ...selectedPrograms
                  ];

                  // Filter to only include inactive programs (IsActive !== 1)
                  const inactiveProgramsToActivate =
                    allProgramsToActivate.filter(
                      (program) => program.IsActive !== 1
                    );

                  if (inactiveProgramsToActivate.length > 0) {
                    await Promise.all(
                      inactiveProgramsToActivate.map((program) =>
                        axios.put(
                          `${apiEndpoint}/loyaltyprogram/support/business/loyaltyprogram`,
                          {
                            Id: program.Id,
                            BusinessId: businessId,
                            PointActionId: program.PointActionId,
                            Points: program.Points,
                            Item: program.Item,
                            IsActive: 1
                          },
                          {
                            headers: {
                              Authorization: `bearer ${accessToken}`,
                              "Content-Type": "application/json"
                            }
                          }
                        )
                      )
                    );

                    setNotification({
                      type: "success",
                      message: `Successfully activated ${inactiveProgramsToActivate.length} program record(s)`
                    });
                  }

                  // Update form values to reflect activation
                  setFormData({
                    ...formData,
                    IsActive: 1
                  });
                } else {
                  // Handle deactivation
                  const allActiveMultiCountPrograms = programs.filter(
                    (p) => p.PointActionId === 5 && p.IsActive === 1
                  );
                  const activeNonMultiCountPrograms = programs.filter(
                    (p) => p.PointActionId !== 5 && p.IsActive === 1
                  );

                  let activeProgramsToDeactivate: LoyaltyProgram[] = [];

                  if (
                    allActiveMultiCountPrograms.length === 2 &&
                    activeNonMultiCountPrograms.length === 0
                  ) {
                    // Deactivate ALL active multi-count programs (both of them)
                    activeProgramsToDeactivate = allActiveMultiCountPrograms;
                  } else {
                    // Deactivate the current program and all related multi-count programs
                    const allProgramsToDeactivate = [
                      confirmModal.program,
                      ...confirmModal.relatedPrograms
                    ];

                    // Filter to only include active programs (IsActive === 1)
                    activeProgramsToDeactivate = allProgramsToDeactivate.filter(
                      (program) => program.IsActive === 1
                    );
                  }

                  // Only make API calls if there are active programs to deactivate
                  if (activeProgramsToDeactivate.length > 0) {
                    await Promise.all(
                      activeProgramsToDeactivate.map((program) =>
                        axios.put(
                          `${apiEndpoint}/loyaltyprogram/support/business/loyaltyprogram`,
                          {
                            Id: program.Id,
                            BusinessId: businessId,
                            PointActionId: program.PointActionId,
                            Points: program.Points,
                            Item: program.Item,
                            IsActive: 0
                          },
                          {
                            headers: {
                              Authorization: `bearer ${accessToken}`,
                              "Content-Type": "application/json"
                            }
                          }
                        )
                      )
                    );

                    const deactivatedCount = activeProgramsToDeactivate.length;
                    const isMultiCountOnly = activeProgramsToDeactivate.every(
                      (p) => p.PointActionId === 5
                    );

                    setNotification({
                      type: "success",
                      message: isMultiCountOnly
                        ? `Successfully deactivated ${deactivatedCount} Multi Count program record(s)`
                        : `Successfully deactivated ${deactivatedCount} program record(s)`
                    });
                  }
                }

                // Close confirmation modal first
                setSelectedProgramsToActivate([]);
                setConfirmModalError(null);
                setConfirmModal({
                  open: false,
                  program: null,
                  relatedPrograms: [],
                  availablePrograms: [],
                  message: "",
                  action: "deactivate"
                });

                // Refresh programs list
                await fetchPrograms();

                // Close the edit modal after successful activation or deactivation
                handleCloseModal();
              } catch (error) {
                console.error(
                  `Error ${
                    confirmModal.action === "activate"
                      ? "activating"
                      : "deactivating"
                  } multi-count programs:`,
                  error
                );
                setModalError(
                  `Failed to ${
                    confirmModal.action === "activate"
                      ? "activate"
                      : "deactivate"
                  } programs`
                );
              } finally {
                setIsConfirmModalLoading(false);
              }
            }}
            disabled={
              isConfirmModalLoading ||
              (confirmModal.action === "activate" &&
                selectedProgramsToActivate.length === 0)
            }
            isLoading={isConfirmModalLoading}
          >
            {isConfirmModalLoading ? (
              <CircularProgress size={20} />
            ) : confirmModal.action === "activate" ? (
              "Activate Selected"
            ) : (
              "Deactivate All"
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BusinessProgramPage;
